class SkewTComponent extends HTMLElement {
  constructor() {
    super();

    const config = {
      margin: {
        large: { left: 0.15, right: 0.1, top: 0.05, bottom: 0.05 },
        medium: { left: 0.2, right: 0.1, top: 0.04, bottom: 0.04 },
        small: { left: 0.1, right: 0.1, top: 0.1, bottom: 0.1 },
      },
      strokeWidth: {
        temp: { large: 3, medium: 2.5, small: 2 },
        dwpt: { large: 3, medium: 2.5, small: 2 },
        skline: { large: 1.8, medium: 1.5, small: 1.2 },
        mean: { large: 2.5, medium: 2, small: 1.8 },
        gridline: { large: 0.75, medium: 0.6, small: 0.5 },
        tempzero: { large: 1.25, medium: 1, small: 0.75 },
        windbarb: { large: 0.75, medium: 0.6, small: 0.5 },
      },
    };

    const shadow = this.attachShadow({ mode: 'open' });

    const template = document.createElement('template');
    template.innerHTML = `
      <div class="container text-center">
        <div class="row center-container">
          <div class="col-md-12">
            <div id="skewt" style="width: 100%; height: 100%;"></div>
          </div>
        </div>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
      .container {
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 10px;
        font-family: Arial, sans-serif;
      }
      .row {
        margin: 0;
        display: flex;
        flex-wrap: wrap;
        height: 100%;
      }
      .col-md-12 {
        position: relative;
        padding: 0;
        width: 100%;
        height: 100%;
      }
      .text-center { text-align: center; }
      .center-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
      }
      .axis path, .axis line {
        fill: none;
        stroke: #000;
        stroke-width: 1px;
        shape-rendering: crispEdges;
      }
      .axis { fill: #000; }
      .y.axis.ticks text { display: none; }
      .temp { fill: none; stroke: red; }
      .dwpt { fill: none; stroke: green; }
      .skline { opacity: 0.8; }
      .gridline, .tempzero { stroke: #dfdfdf; fill: none; }
      .tempzero { stroke: #aaa; }
      .windbarb { stroke: blue; fill: none; }
      .flag { fill: blue; }
      .overlay { fill: none; pointer-events: all; }
      .focus.tmpc circle { fill: red; stroke: none; }
      .focus.dwpc circle { fill: green; stroke: none; }
      .y.axis, .x.axis, .focus text {
        font-size: var(--font-size-large, 16px);
      }
      .y.axis.hght {
        font-size: var(--font-size-large-minus-2, 14px);
        fill: red;
      }
      .temp { stroke-width: ${config.strokeWidth.temp.large}px; }
      .dwpt { stroke-width: ${config.strokeWidth.dwpt.large}px; }
      .skline { stroke-width: ${config.strokeWidth.skline.large}px; }
      .mean { stroke-width: ${config.strokeWidth.mean.large}px; }
      .gridline { stroke-width: ${config.strokeWidth.gridline.large}px; }
      .tempzero { stroke-width: ${config.strokeWidth.tempzero.large}px; }
      .windbarb { stroke-width: ${config.strokeWidth.windbarb.large}px; }
      @media (max-width: 992px) {
        .container { padding: 8px; }
        .y.axis, .x.axis, .focus text { font-size: var(--font-size-medium, 14px); }
        .y.axis.hght { font-size: var(--font-size-medium-minus-2, 12px); }
        .temp { stroke-width: ${config.strokeWidth.temp.medium}px; }
        .dwpt { stroke-width: ${config.strokeWidth.dwpt.medium}px; }
        .skline { stroke-width: ${config.strokeWidth.skline.medium}px; }
        .mean { stroke-width: ${config.strokeWidth.mean.medium}px; }
        .gridline { stroke-width: ${config.strokeWidth.gridline.medium}px; }
        .tempzero { stroke-width: ${config.strokeWidth.tempzero.medium}px; }
        .windbarb { stroke-width: ${config.strokeWidth.windbarb.medium}px; }
      }
      @media (max-width: 576px) {
        .container { padding: 5px; }
        .y.axis, .x.axis, .focus text { font-size: var(--font-size-small, 12px); }
        .y.axis.hght { font-size: var(--font-size-small-minus-2, 10px); }
        .temp { stroke-width: ${config.strokeWidth.temp.small}px; }
        .dwpt { stroke-width: ${config.strokeWidth.dwpt.small}px; }
        .skline { stroke-width: ${config.strokeWidth.skline.small}px; }
        .mean { stroke-width: ${config.strokeWidth.mean.small}px; }
        .gridline { stroke-width: ${config.strokeWidth.gridline.small}px; }
        .tempzero { stroke-width: ${config.strokeWidth.tempzero.small}px; }
        .windbarb { stroke-width: ${config.strokeWidth.windbarb.small}px; }
      }
      #svg {
        display: block !important;
        visibility: visible !important;
        width: 100%;
        height: 100%;
      }
    `;

    shadow.appendChild(style);
    shadow.appendChild(template.content.cloneNode(true));

    this.shadow = shadow;
    this.skewtDiv = shadow.querySelector('#skewt');
    this.config = config;
  }

  connectedCallback() {
    if (!window.d3) {
      console.error("D3.js is not loaded. Please include <script src='https://d3js.org/d3.v7.min.js'></script> in your HTML.");
      return;
    }
    this.skewt = this.initializeSkewT(this.skewtDiv);
  }

  initializeSkewT(div) {
    const wrapper = d3.select(div);
    let width = 300;
    let height = 400;
    let margin = this.config.margin.large;
    const deg2rad = (Math.PI / 180);
    let tan;
    let skewFactor;
    const basep = 1050;
    const topp = 100;
    const plines = [1000, 850, 700, 500, 300, 200, 100];
    const pticks = [950, 900, 800, 750, 650, 600, 550, 450, 400, 350, 250, 150];
    const barbsize = 25;
    const r = d3.scaleLinear().range([0, 300]).domain([0, 150]);
    const y2 = d3.scaleLinear();
    let data = [];
    const unit = "kt";
    const bisectTemp = d3.bisector(d => d.press).left;

    const svg = wrapper.append("svg").attr("id", "svg").style("visibility", "visible");
    const container = svg.append("g").attr("id", "container");
    const skewtbg = container.append("g").attr("id", "skewtbg").attr("class", "skewtbg");
    const skewtgroup = container.append("g").attr("class", "skewt");
    const barbgroup = container.append("g").attr("class", "windbarb");

    let w, h, x, y, xAxis, yAxis, yAxis2;

    x = d3.scaleLinear();
    y = d3.scaleLog().domain([topp, basep]);
    xAxis = d3.axisBottom(x).tickSize(0).ticks(10);
    yAxis = d3.axisLeft(y).tickSize(0).tickValues(plines).tickFormat(d3.format(".0d"));
    yAxis2 = d3.axisRight(y).tickSize(5).tickValues(pticks);

    const setVariables = () => {
      try {
        const parent = this.parentElement || { getBoundingClientRect: () => ({ width: 300, height: 400 }) };
        const rect = parent.getBoundingClientRect();
        width = rect.width || 300;

        let targetHeight;
        if (window.innerWidth <= 576) {
          targetHeight = width * 1.5;
          margin = this.config.margin.small;
          skewFactor = 2;
          const skewAngle = 50;
          tan = Math.tan(skewAngle * deg2rad);
          x.domain([-45, 50]);
        } else if (window.innerWidth <= 992) {
          targetHeight = width / 1.5;
          margin = this.config.margin.medium;
          skewFactor = 1;
          const skewAngle = 45;
          tan = Math.tan(skewAngle * deg2rad);
          x.domain([-45, 40]);
        } else {
          targetHeight = width / 1.5;
          margin = this.config.margin.large;
          skewFactor = 1;
          const skewAngle = 45;
          tan = Math.tan(skewAngle * deg2rad);
          x.domain([-55, 45]);
        }

        height = Math.min(targetHeight, rect.height || window.innerHeight * 0.8) || 400;

        const baseFontSize = Math.min(width, height) / 40;
        this.config.fontSize = {
          large: Math.max(16, baseFontSize * 1.0),
          medium: Math.max(14, baseFontSize * 0.9),
          small: Math.max(12, baseFontSize * 0.8),
        };

        if (!this.cssVariablesSet) {
          try {
            this.shadow.querySelector('style').sheet.insertRule(`
              :host {
                --font-size-large: ${this.config.fontSize.large}px;
                --font-size-large-minus-2: ${this.config.fontSize.large - 2}px;
                --font-size-medium: ${this.config.fontSize.medium}px;
                --font-size-medium-minus-2: ${this.config.fontSize.medium - 2}px;
                --font-size-small: ${this.config.fontSize.small}px;
                --font-size-small-minus-2: ${this.config.fontSize.small - 2}px;
              }
            `, 0);
            this.cssVariablesSet = true;
          } catch (e) {
            console.warn("Could not insert CSS variables:", e);
          }
        }

        const marginLeft = width * margin.left;
        const marginRight = width * margin.right;
        const marginTop = height * margin.top;
        const marginBottom = height * margin.bottom;

        w = width - marginLeft - marginRight;
        h = height - marginTop - marginBottom;

        if (w <= 0 || h <= 0) {
          console.error(`Invalid dimensions: w=${w}, h=${h}. Resetting to defaults.`);
          w = 300;
          h = 400;
        }

        x.range([0, w]);
        y.range([0, h]);
      } catch (error) {
        console.error("Error in setVariables:", error);
      }
    };

    function convert(msvalue, unit) {
      switch (unit) {
        case "kt": return msvalue * 1.943844492;
        case "kmh": return msvalue * 3.6;
        default: return msvalue;
      }
    }

    let resizeTimeout;
    let isResizing = false;
    this.resizeObserver = new ResizeObserver(() => {
      if (isResizing) return;
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        resize();
      }, 100);
    });

    function resize() {
      if (isResizing) return;
      isResizing = true;
      
      try {
        skewtbg.selectAll("*").remove();
        skewtgroup.selectAll("*").remove();
        barbgroup.selectAll("*").remove();
        container.selectAll(".tooltip-labels").remove();
        container.selectAll(".overlay").remove();
        
        setVariables();
        svg.attr("width", width)
           .attr("height", height);
        container.attr("transform", `translate(${width * margin.left},${height * margin.top})`);
        
        // Always draw background and barb templates
        drawBackground();
        makeBarbTemplates();
        
        // Only redraw data if we have it
        if (data && data.length > 0) {
          plot(data);
        }
      } finally {
        isResizing = false;
      }
    }

    function drawBackground() {
      try {
        skewtbg.append("clipPath")
          .attr("id", "clipper")
          .append("rect")
          .attr("x", 0)
          .attr("y", 0)
          .attr("width", w)
          .attr("height", h);

        skewtbg.selectAll("templine")
          .data(d3.range(-150, 45, 10))
          .join("line")
          .attr("x1", d => x(d) - 0.5 + (y(basep) - y(100)) / tan)
          .attr("x2", d => x(d) - 0.5)
          .attr("y1", 0)
          .attr("y2", h)
          .attr("class", d => d === 0 ? "tempzero" : "gridline")
          .attr("clip-path", "url(#clipper)");

        skewtbg.selectAll("pressureline")
          .data(plines)
          .join("line")
          .attr("x1", 0)
          .attr("x2", w)
          .attr("y1", d => y(d))
          .attr("y2", d => y(d))
          .attr("class", "gridline");

        const pp = d3.range(topp, basep + 1, 10);
        const dryad = d3.range(-50, 240, 20);
        const all = [];
        for (let i = 0; i < dryad.length; i++) {
          const z = [];
          for (let j = 0; j < pp.length; j++) { z.push(dryad[i]); }
          all.push(z);
        }

        const dryline = d3.line()
          .x((d, i) => x((273.15 + d) / Math.pow((1000 / pp[i]), 0.286) - 273.15) + (y(basep) - y(pp[i])) / skewFactor * tan)
          .y((d, i) => y(pp[i]));

        skewtbg.selectAll("dryadiabatline")
          .data(all)
          .join("path")
          .attr("class", "gridline")
          .attr("clip-path", "url(#clipper)")
          .attr("d", dryline);

        skewtbg.append("line")
          .attr("x1", w - 0.5)
          .attr("x2", w - 0.5)
          .attr("y1", 0)
          .attr("y2", h)
          .attr("class", "gridline");

        // X axis (temperature) at bottom
        skewtbg.append("g")
          .attr("class", "x axis")
          .attr("transform", `translate(0,${h - 0.5})`)
          .call(xAxis);

        // Y axis (pressure) on left - main pressure labels
        const yAxisGroup = skewtbg.append("g")
          .attr("class", "y axis pressure")
          .attr("transform", "translate(-0.5,0)");
        yAxisGroup.call(yAxis);
        
        // Ensure Y axis text is visible and properly styled
        yAxisGroup.selectAll("text")
          .style("fill", "#000")
          .style("font-size", "14px")
          .style("font-family", "Arial, sans-serif")
          .style("display", "block")
          .style("visibility", "visible");

        // Y axis ticks on right (keep these hidden)
        skewtbg.append("g")
          .attr("class", "y axis ticks")
          .attr("transform", "translate(-0.5,0)")
          .call(yAxis2);

      } catch (error) {
        console.error("Error in drawBackground:", error);
      }
    }

    function makeBarbTemplates() {
      try {
        const speeds = d3.range(5, 105, 5);
        const barbdef = container.append('defs');
        speeds.forEach(d => {
          const thisbarb = barbdef.append('g').attr('id', 'barb' + d);
          const flags = Math.floor(d / 50);
          const pennants = Math.floor((d - flags * 50) / 10);
          const halfpennants = Math.floor((d - flags * 50 - pennants * 10) / 5);
          let px = barbsize;

          thisbarb.append("line")
            .attr("x1", 0).attr("x2", 0)
            .attr("y1", 0).attr("y2", barbsize);

          for (let i = 0; i < flags; i++) {
            thisbarb.append("polyline")
              .attr("points", `0,${px} -10,${px} 0,${px - 4}`)
              .attr("class", "flag");
            px -= 7;
          }
          for (let i = 0; i < pennants; i++) {
            thisbarb.append("line")
              .attr("x1", 0).attr("x2", -10)
              .attr("y1", px).attr("y2", px + 4);
            px -= 3;
          }
          for (let i = 0; i < halfpennants; i++) {
            thisbarb.append("line")
              .attr("x1", 0).attr("x2", -5)
              .attr("y1", px).attr("y2", px + 2);
            px -= 3;
          }
        });
      } catch (error) {
        console.error("Error in makeBarbTemplates:", error);
      }
    }

    const drawToolTips = (skewtlines) => {
      try {
        const lines = skewtlines.reverse();

        // Clean up existing tooltips and listeners
        skewtgroup.selectAll(".focus").remove();
        container.selectAll(".tooltip-labels").remove();
        container.selectAll(".overlay").remove();

        const tmpcfocus = skewtgroup.append("g").attr("class", "focus tmpc").style("display", "none");
        tmpcfocus.append("circle").attr("r", window.innerWidth <= 576 ? 3 : 4);
        tmpcfocus.append("text").style("fill", "red").attr("x", 9).attr("dy", ".35em");

        const dwpcfocus = skewtgroup.append("g").attr("class", "focus dwpc").style("display", "none");
        dwpcfocus.append("circle").attr("r", window.innerWidth <= 576 ? 3 : 4);
        dwpcfocus.append("text").style("fill", "green").attr("x", -9).attr("text-anchor", "end").attr("dy", ".35em");

        const pressureLine = skewtgroup.append("line")
          .attr("class", "focus pressure-line")
          .style("display", "none")
          .attr("x1", 0)
          .attr("x2", w)
          .style("stroke", "grey")
          .style("stroke-width", "1px")
          .style("stroke-dasharray", "4 4")
          .style("opacity", "0.8");

        const hghtfocus = skewtgroup.append("g").attr("class", "focus hght").style("display", "none");
        hghtfocus.append("text").attr("x", 0).attr("text-anchor", "end").attr("dy", ".35em");

        const wspdfocus = skewtgroup.append("g").attr("class", "focus windspeed").style("display", "none");
        wspdfocus.append("text").attr("x", 0).attr("text-anchor", "start").attr("dy", ".35em");

        const labelGroup = container.append("g").attr("class", "tooltip-labels").style("display", "none");

        labelGroup.append("text")
          .attr("class", "hght-label")
          .attr("x", 0.25)
          .attr("y", h + 40)
          .attr("text-anchor", "start")
          .style("fill", "black");

        labelGroup.append("text")
          .attr("class", "dwpc-label")
          .attr("x", w * 0.40)
          .attr("y", h + 40)
          .attr("text-anchor", "start")
          .style("fill", "green");

        labelGroup.append("text")
          .attr("class", "tmpc-label")
          .attr("x", w * 0.70)
          .attr("y", h + 40)
          .attr("text-anchor", "start")
          .style("fill", "red");

        labelGroup.append("text")
          .attr("class", "wspd-label")
          .attr("x", w * 0.90)
          .attr("y", h + 40)
          .attr("text-anchor", "start")
          .style("fill", "black");

        function showTooltips() {
          tmpcfocus.style("display", null);
          dwpcfocus.style("display", null);
          pressureLine.style("display", null);
          if (window.innerWidth <= 576) {
            labelGroup.style("display", null);
          } else {
            hghtfocus.style("display", null);
            wspdfocus.style("display", null);
          }
        }

        function hideTooltips() {
          tmpcfocus.style("display", "none");
          dwpcfocus.style("display", "none");
          pressureLine.style("display", "none");
          labelGroup.style("display", "none");
          hghtfocus.style("display", "none");
          wspdfocus.style("display", "none");
        }

        function updateTooltips(event, touch = false) {
          const pointer = touch ? event.touches[0] : event;
          let pointerY;
          
          if (touch) {
            // Get the SVG element's position relative to the viewport
            const svgRect = svg.node().getBoundingClientRect();
            const containerOffset = height * margin.top;
            // Calculate the Y position relative to the chart area
            pointerY = pointer.clientY - svgRect.top - containerOffset;
          } else {
            pointerY = d3.pointer(pointer, this)[1];
          }
          
          const y0 = y.invert(pointerY);
          
          // Only show tooltips for pressure levels within our chart range (100mb to 1050mb)
          if (y0 < topp || y0 > basep) {
            hideTooltips();
            return;
          }
          
          const i = bisectTemp(lines, y0, 1, lines.length - 1);
          const d0 = lines[i - 1];
          const d1 = lines[i];
          const d = y0 - d0.press > d1.press - y0 ? d1 : d0;
          
          // Double-check that the selected data point is within range
          if (d.press < topp || d.press > basep) {
            hideTooltips();
            return;
          }

          // Re-show tooltips when back in valid range (in case they were hidden)
          tmpcfocus.style("display", null);
          dwpcfocus.style("display", null);
          pressureLine.style("display", null);
          if (window.innerWidth <= 576) {
            labelGroup.style("display", null);
          } else {
            hghtfocus.style("display", null);
            wspdfocus.style("display", null);
          }

          const tempX = x(d.temp) + (y(basep) - y(d.press)) / skewFactor * tan;
          const dwptX = x(d.dwpt) + (y(basep) - y(d.press)) / skewFactor * tan;
          const yPos = Math.max(0, Math.min(h, y(d.press)));

          tmpcfocus.attr("transform", `translate(${tempX},${yPos})`);
          if (d.dwpt !== null && !isNaN(d.dwpt)) {
            dwpcfocus.style("display", null);
            dwpcfocus.attr("transform", `translate(${dwptX},${yPos})`);
          } else {
            dwpcfocus.style("display", "none");
          }

          pressureLine.attr("transform", `translate(0,${yPos})`);

          if (window.innerWidth <= 576) {
            labelGroup.select(".hght-label").text(Math.round(d.hght).toLocaleString() + " ft");
            labelGroup.select(".dwpc-label").text(d.dwpt !== null && !isNaN(d.dwpt) ? Math.round(d.dwpt) + "°C" : "");
            labelGroup.select(".tmpc-label").text(Math.round(d.temp) + "°C");
            labelGroup.select(".wspd-label").text(Math.round(convert(d.wspd, unit) * 10) / 10 + " " + unit);
          } else {
            tmpcfocus.select("text").text(Math.round(d.temp) + "°C");
            if (d.dwpt !== null && !isNaN(d.dwpt)) {
              dwpcfocus.select("text").text(Math.round(d.dwpt) + "°C");
            }
            hghtfocus.attr("transform", `translate(-50,${yPos})`);
            hghtfocus.select("text").text(Math.round(d.hght).toLocaleString() + " ft");
            wspdfocus.attr("transform", `translate(${w + 10},${yPos})`);
            wspdfocus.select("text").text(Math.round(convert(d.wspd, unit) * 10) / 10 + " " + unit);
          }
        }

        const overlay = container.append("rect")
          .attr("class", "overlay")
          .attr("width", w)
          .attr("height", h)
          .on("mouseover", showTooltips)
          .on("mouseout", hideTooltips)
          .on("mousemove", updateTooltips);

        // Add touch event listeners with proper passive handling
        const overlayNode = overlay.node();
        overlayNode.addEventListener("touchstart", function(event) {
          event.preventDefault();
          showTooltips();
          updateTooltips(event, true);
        }, { passive: false });
        
        overlayNode.addEventListener("touchmove", function(event) {
          event.preventDefault();
          updateTooltips(event, true);
        }, { passive: false });
        
        overlayNode.addEventListener("touchend", function(event) {
          event.preventDefault();
          // Don't hide tooltips on touch end - let them persist
        }, { passive: false });

      } catch (error) {
        console.error("Error in drawToolTips:", error);
      }
    }

    function plot(s) {
      try {
        data = s;
        
        // Always set up dimensions and draw background, regardless of data
        setVariables();
        svg.attr("width", width)
           .attr("height", height);
        container.attr("transform", `translate(${width * margin.left},${height * margin.top})`);
        
        // Always clear and redraw background to ensure pressure levels are visible
        skewtbg.selectAll("*").remove();
        drawBackground();
        makeBarbTemplates();

        // Clear data-dependent elements
        skewtgroup.selectAll("*").remove();
        barbgroup.selectAll("*").remove();
        container.selectAll(".tooltip-labels").remove();
        container.selectAll(".overlay").remove();

        // Only proceed with data plotting if we have actual data
        if (!data || data.length === 0) {
          return;
        }

        const skewtline = data.filter(d => (d.temp > -1000 && d.dwpt > -1000));
        const skewtlines = [skewtline];

        const templine = d3.line()
          .x((d, i) => x(d.temp) + (y(basep) - y(d.press)) / skewFactor * tan)
          .y((d, i) => y(d.press));

        skewtgroup.selectAll("templines")
          .data(skewtlines)
          .join("path")
          .attr("class", (d, i) => i < 10 ? "temp skline" : "temp mean")
          .attr("clip-path", "url(#clipper)")
          .attr("d", templine);

        const tempdewline = d3.line()
          .x((d, i) => x(d.dwpt) + (y(basep) - y(d.press)) / skewFactor * tan)
          .y((d, i) => y(d.press));

        skewtgroup.selectAll("tempdewlines")
          .data(skewtlines)
          .join("path")
          .attr("class", (d, i) => i < 10 ? "dwpt skline" : "dwpt mean")
          .attr("clip-path", "url(#clipper)")
          .attr("d", tempdewline);

        const barbs = skewtline.filter(d => (d.wdir >= 0 && d.wspd >= 0 && d.press >= topp));
        barbgroup.selectAll("barbs")
          .data(barbs)
          .join("use")
          .attr("href", d => "#barb" + Math.round(convert(d.wspd, "kt") / 5) * 5)
          .attr("transform", d => `translate(${w},${y(d.press)}) rotate(${d.wdir + 180})`);

        drawToolTips(skewtlines[0]);
      } catch (error) {
        console.error("Error in plot:", error);
      }
    }

    function clear() {
      try {
        skewtgroup.selectAll("*").remove();
        barbgroup.selectAll("*").remove();
        skewtbg.selectAll("*").remove();
      } catch (error) {
        console.error("Error in clear:", error);
      }
    }

    // Don't call resize() initially - wait for plot() to be called
    // try {
    //   resize();
    // } catch (error) {
    //   console.error("Error in initial render:", error);
    // }

    this.resizeObserver.observe(this.parentElement);
    return { plot, clear };
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

customElements.define('skew-t-component', SkewTComponent);